import { ReactNode } from 'react';
import { Box, Grid, Typography, Stack, Switch } from '@mui/material';
import { CurrencyToggle } from '../ui/CurrencyToggle';
import { FadeIn } from '../ui/FadeIn';

interface PricingContentLayoutProps {
    children: ReactNode;
    headerText?: string;
    gridSpacing?: number;
    maxWidth?: number; // Number of columns (1-12) for the header container
    headerMb?: number;
    billingPeriod?: 'monthly' | 'annual';
    onBillingPeriodChange?: (period: 'monthly' | 'annual') => void;
}

export function PricingContentLayout({
    children,
    headerText,
    gridSpacing = 4,
    maxWidth = 12,
    headerMb = 2,
    billingPeriod,
    onBillingPeriodChange
}: PricingContentLayoutProps) {
    const isAnnual = billingPeriod === 'annual';

    return (
        <Box maxWidth="lg" mx="auto">

            {/* Header / Controls Row */}
            <Grid container spacing={gridSpacing} justifyContent="center" sx={{ mb: headerMb }}>
                <Grid item xs={12} md={maxWidth}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column-reverse', md: 'row' },
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 2,
                        position: 'relative'
                    }}>

                        {/* Left: Spacer or Header Text */}
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' }, width: '100%' }}>
                            {headerText && (
                                <Typography variant="h5" color="text.secondary" fontWeight={600}>
                                    {headerText}
                                </Typography>
                            )}
                        </Box>

                        {/* Center: Billing Toggle */}
                        {billingPeriod && onBillingPeriodChange && (
                            <Box sx={{
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                            }}>
                                <FadeIn delay={100} direction="down">
                                    <Stack direction="row" alignItems="center" spacing={1.5}>
                                        <Typography variant="body2" fontWeight={400} color={!isAnnual ? "text.primary" : "text.secondary"}>
                                            Monthly
                                        </Typography>

                                        <Switch
                                            checked={isAnnual}
                                            onChange={(e) => onBillingPeriodChange(e.target.checked ? "annual" : "monthly")}
                                            color="primary"
                                        />

                                        <Typography variant="body2" fontWeight={isAnnual ? 400 : 400} color={isAnnual ? "text.primary" : "text.secondary"}>
                                            Yearly
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            fontWeight={isAnnual ? 600 : 500}
                                            color={isAnnual ? "#22C55E" : "text.disabled"}
                                            fontStyle={isAnnual ? "italic" : "normal"}
                                            sx={{ fontSize: '0.8rem' }}
                                        >
                                            {isAnnual ? "20% off" : "Save 20%"}
                                        </Typography>
                                    </Stack>
                                </FadeIn>
                            </Box>
                        )}

                        {/* Right: Currency Toggle */}
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' }, width: '100%' }}>
                            <FadeIn delay={100} direction="down">
                                <CurrencyToggle />
                            </FadeIn>
                        </Box>

                    </Box>
                </Grid>
            </Grid>

            {/* Content Grid */}
            <Grid container spacing={gridSpacing} justifyContent="center" alignItems="stretch">
                {children}
            </Grid>

        </Box>
    );
}
